import type React from 'react';
import type { PropsWithChildren } from 'react';
import { useRef } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { $createNodeSelection, $getNodeByKey, $setSelection } from 'lexical';

import queueMicroTask from '@polyai/common/utils/queueMicroTask';
import type { ActionType } from '@polyai/stream/utils/rich-text-input';
import Skeleton from '@polyai/ui/components/atoms/Skeleton';

import type { ActionTagRef } from 'components/molecules/ActionTag';
import ActionTag from 'components/molecules/ActionTag';
import { useActionRenderer } from 'components/molecules/LexicalEditor/features/action/hooks/useActionRenderer';
import { useActionsContext } from 'components/molecules/RichTextInputV2';
import useActionKeyboardNavigation from '../../hooks/useActionKeyboardNavigation';
import { $isActionNode } from '../../nodes/ActionNode';

export interface ActionProps extends PropsWithChildren {
  type: ActionType;
  nodeKey: string;
  id: string;
}

const Action: React.FC<ActionProps> = ({ type, id, nodeKey }) => {
  const actionTagRef = useRef<ActionTagRef>(null);
  const [editor] = useLexicalComposerContext();
  const actionRenderer = useActionRenderer({ type, id });
  const editable = editor.isEditable();
  const [isSelected] = useLexicalNodeSelection(nodeKey);

  const { isActionTagStatic } = useActionsContext();

  useActionKeyboardNavigation({
    nodeKey,
    openPopup: actionTagRef.current?.openPopup,
  });

  const handleRemove = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if ($isActionNode(node)) {
        node.selectStart();
        node.remove();
      }
    });
  };

  const onClose = () => {
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node && $isActionNode(node)) {
        const nodeSelection = $createNodeSelection();
        nodeSelection.add(nodeKey);
        $setSelection(nodeSelection);
        void queueMicroTask(() => {
          editor.focus();
          editor.getRootElement()?.focus({ preventScroll: true });
        });
      }
    });
  };

  if (!actionRenderer) {
    return null;
  }

  const {
    isEnabled,
    Content,
    isLoading,
    name,
    prefix: _,
    ...tagProps
  } = actionRenderer;

  if (!isEnabled) {
    return (
      <ActionTag
        action={isLoading ? <Skeleton /> : <span>{name}</span>}
        disabled
        end={0}
        isSelected={isSelected}
        isStatic
        loading={isLoading}
        readonly
        setCursorPosition={() => {}}
        start={0}
        tabIndex={-1}
        {...tagProps}
      >
        {() => null}
      </ActionTag>
    );
  }

  return (
    <ActionTag
      ref={actionTagRef}
      action={isLoading ? <Skeleton /> : <span>{name}</span>}
      disabled={!isEnabled}
      end={0}
      isSelected={isSelected}
      isStatic={isActionTagStatic}
      loading={isLoading}
      readonly={!editable}
      setCursorPosition={() => {}}
      start={0}
      tabIndex={-1}
      onClose={onClose}
      {...tagProps}
    >
      {({ closePopup }) => (
        <Content
          closePopup={closePopup}
          id={id}
          name={name}
          nodeKey={nodeKey}
          remove={handleRemove}
        />
      )}
    </ActionTag>
  );
};

export default Action;
